import ks from "../ReUsableTS/kBrowserShared";
import { kHTMLEditableColType, kEditableTableRow, kEditableColumn } from "../ReUsableTS/kEditableTable";

// The set of comparison operators a single column filter can apply. Which
// subset is valid for a given column is decided by kColumnFilter.OperatorsFor
// (text vs number vs date vs boolean vs list). Numeric values are kept stable
// so a future serialize feature can round-trip them.
export enum kFilterOperator {
    Contains = 1,
    Equals = 2,
    NotEquals = 3,
    StartsWith = 4,
    EndsWith = 5,
    IsEmpty = 6,
    IsNotEmpty = 7,
    GreaterThan = 8,
    LessThan = 9,
    GreaterOrEqual = 10,
    LessOrEqual = 11,
    Between = 12,
    Before = 13,
    After = 14,
    OnDate = 15,
    InList = 16,
    IsTrue = 17,
    IsFalse = 18
}

// Plain-data shape of one column condition — the JSON round-trip form used by
// Serialize/Deserialize so a host can persist and restore a grid's filters.
export interface kColumnFilterState {
    PropName: string;
    ColType: number;
    Title: string;
    Operator: number;
    Value: string;
    Value2: string;
    SelectedValues: string[];
}

// Plain-data shape of a whole grid's filter state.
export interface kFilterState {
    Columns: kColumnFilterState[];
    GlobalTerm: string;
}

// One filter condition on one column. Holds the operator and its operands and
// knows how to test a single raw cell value (as returned by
// kEditableTableRow.GetValueFromCell, which already type-coerces Number/
// Currency to number and SelectMulti to a string[]). ONE concern: evaluate
// itself against a value and describe itself for a chip.
export class kColumnFilter {
    public PropName: string = "";
    public ColType: kHTMLEditableColType = kHTMLEditableColType.Text;
    public Title: string = "";
    public Operator: kFilterOperator = kFilterOperator.Contains;
    public Value: string = "";
    public Value2: string = "";
    public SelectedValues: string[] = [];
    public Enabled: boolean = true;

    // True when the condition has no operands and would match every row, so the
    // grid can drop an empty condition instead of treating it as active.
    public IsEmptyCondition = (): boolean => {
        if (ks.KErrorMode === true) { return true; }
        try {
            if (this.Operator === kFilterOperator.IsEmpty) { return false; }
            if (this.Operator === kFilterOperator.IsNotEmpty) { return false; }
            if (this.Operator === kFilterOperator.IsTrue) { return false; }
            if (this.Operator === kFilterOperator.IsFalse) { return false; }
            if (this.Operator === kFilterOperator.InList) {
                return this.SelectedValues.length === 0;
            }
            // Between needs BOTH bounds; with only one it is incomplete and must
            // be treated as empty (otherwise the missing bound defaults to 0/""
            // and silently excludes every row).
            if (this.Operator === kFilterOperator.Between) {
                return ks.Nz(this.Value).trim() === "" || ks.Nz(this.Value2).trim() === "";
            }
            return ks.Nz(this.Value) === "" && ks.Nz(this.Value2) === "";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter.IsEmptyCondition");
            return true;
        }
    };

    public Matches = (RawValue: any): boolean => {
        if (ks.KErrorMode === true) { return true; }
        try {
            if (this.Enabled === false) { return true; }
            if (this.IsEmptyCondition() === true) { return true; }
            if (this.Operator === kFilterOperator.InList) { return this._MatchesList(RawValue); }
            if (this.Operator === kFilterOperator.IsTrue) { return this._AsBool(RawValue) === true; }
            if (this.Operator === kFilterOperator.IsFalse) { return this._AsBool(RawValue) === false; }
            if (this._IsNumberColumn() === true) { return this._MatchesNumber(RawValue); }
            if (this._IsDateColumn() === true) { return this._MatchesDate(RawValue); }
            return this._MatchesText(RawValue);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter.Matches");
            return true;
        }
    };

    private _IsNumberColumn = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (this.ColType === kHTMLEditableColType.Number) { return true; }
            if (this.ColType === kHTMLEditableColType.Currency) { return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter._IsNumberColumn");
            return false;
        }
    };

    private _IsDateColumn = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (this.ColType === kHTMLEditableColType.Date) { return true; }
            if (this.ColType === kHTMLEditableColType.DateTime) { return true; }
            if (this.ColType === kHTMLEditableColType.Time) { return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter._IsDateColumn");
            return false;
        }
    };

    // Display text for a SINGLE raw cell value (no array handling — callers loop).
    // A hyperlink cell's value is a { Url, Title } object (kLinkValue), so its text
    // is the Title (or Url) — NOT "[object Object]". Shared by _AsText, RowText and
    // DistinctValues so column filtering, the checklist and global search all read
    // the same link text. Structural detection keeps the model free of a link-module
    // import.
    public static ValueToText = (RawValue: any): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            if (RawValue === null || RawValue === undefined) { return ""; }
            if (RawValue === true) { return "true"; }
            if (RawValue === false) { return "false"; }
            if (typeof RawValue === "object" && Array.isArray(RawValue) === false) {
                const Obj: any = RawValue;
                if (typeof Obj.Title === "string" && Obj.Title !== "") { return Obj.Title; }
                if (typeof Obj.Url === "string" && Obj.Url !== "") { return Obj.Url; }
            }
            return ks.Nz(RawValue).toString();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter.ValueToText");
            return "";
        }
    };

    // Culture-aware text rendering of any raw value. Arrays (SelectMulti) join with
    // "; "; each element (and scalar link objects) resolves through ValueToText.
    private _AsText = (RawValue: any): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            if (Array.isArray(RawValue) === true) {
                const Parts: string[] = [];
                let CurrEl: any;
                for (CurrEl of RawValue) { Parts.push(kColumnFilter.ValueToText(CurrEl)); }
                return Parts.join("; ");
            }
            return kColumnFilter.ValueToText(RawValue);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter._AsText");
            return "";
        }
    };

    private _AsBool = (RawValue: any): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (RawValue === true) { return true; }
            if (RawValue === false) { return false; }
            const Text: string = this._AsText(RawValue).trim().toLowerCase();
            if (Text === "true") { return true; }
            if (Text === "☑") { return true; }
            if (Text === "yes") { return true; }
            if (Text === "1") { return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter._AsBool");
            return false;
        }
    };

    private _MatchesText = (RawValue: any): boolean => {
        if (ks.KErrorMode === true) { return true; }
        try {
            const Cell: string = this._AsText(RawValue).toLowerCase();
            const Needle: string = ks.Nz(this.Value).toLowerCase();
            switch (this.Operator) {
                case kFilterOperator.IsEmpty: return Cell.trim() === "";
                case kFilterOperator.IsNotEmpty: return Cell.trim() !== "";
                case kFilterOperator.Equals: return Cell === Needle;
                case kFilterOperator.NotEquals: return Cell !== Needle;
                case kFilterOperator.StartsWith: return Cell.indexOf(Needle) === 0;
                case kFilterOperator.EndsWith: return Cell.lastIndexOf(Needle) === Cell.length - Needle.length;
                default: return Cell.indexOf(Needle) >= 0;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter._MatchesText");
            return true;
        }
    };

    private _MatchesNumber = (RawValue: any): boolean => {
        if (ks.KErrorMode === true) { return true; }
        try {
            const Text: string = this._AsText(RawValue).trim();
            if (this.Operator === kFilterOperator.IsEmpty) { return Text === ""; }
            if (this.Operator === kFilterOperator.IsNotEmpty) { return Text !== ""; }
            const Cell: number = ks.N0(RawValue);
            const Low: number = ks.N0(this.Value);
            // Defensive: an incomplete Between (one bound blank) must not exclude
            // every row. IsEmptyCondition normally drops it before we get here.
            if (this.Operator === kFilterOperator.Between
                && (ks.Nz(this.Value).trim() === "" || ks.Nz(this.Value2).trim() === "")) { return true; }
            switch (this.Operator) {
                case kFilterOperator.Equals: return Cell === Low;
                case kFilterOperator.NotEquals: return Cell !== Low;
                case kFilterOperator.GreaterThan: return Cell > Low;
                case kFilterOperator.LessThan: return Cell < Low;
                case kFilterOperator.GreaterOrEqual: return Cell >= Low;
                case kFilterOperator.LessOrEqual: return Cell <= Low;
                case kFilterOperator.Between: return Cell >= Low && Cell <= ks.N0(this.Value2);
                default: return Cell === Low;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter._MatchesNumber");
            return true;
        }
    };

    // Date/Time/DateTime cells store ISO-ordered strings from native inputs, so
    // a lexical compare is also a chronological compare. Empty cells never match
    // an ordering operator.
    private _MatchesDate = (RawValue: any): boolean => {
        if (ks.KErrorMode === true) { return true; }
        try {
            const Cell: string = this._AsText(RawValue).trim();
            if (this.Operator === kFilterOperator.IsEmpty) { return Cell === ""; }
            if (this.Operator === kFilterOperator.IsNotEmpty) { return Cell !== ""; }
            if (Cell === "") { return false; }
            const Low: string = ks.Nz(this.Value).trim();
            // Defensive: an incomplete Between (one bound blank) must not exclude
            // every row. IsEmptyCondition normally drops it before we get here.
            if (this.Operator === kFilterOperator.Between
                && (Low === "" || ks.Nz(this.Value2).trim() === "")) { return true; }
            switch (this.Operator) {
                case kFilterOperator.OnDate: return Cell === Low;
                case kFilterOperator.Equals: return Cell === Low;
                case kFilterOperator.Before: return Cell < Low;
                case kFilterOperator.After: return Cell > Low;
                case kFilterOperator.Between: return Cell >= Low && Cell <= ks.Nz(this.Value2).trim();
                default: return Cell === Low;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter._MatchesDate");
            return true;
        }
    };

    // A distinct-value checklist: the row passes when its value is among the
    // checked options. SelectMulti rows (array values) pass when ANY of their
    // values is checked.
    private _MatchesList = (RawValue: any): boolean => {
        if (ks.KErrorMode === true) { return true; }
        try {
            if (this.SelectedValues.length === 0) { return true; }
            const Checked: string[] = this._NormalizedSelected();
            if (Array.isArray(RawValue) === true) {
                let CurrEntry: string;
                for (CurrEntry of RawValue) {
                    if (Checked.indexOf(ks.Nz(CurrEntry).trim().toLowerCase()) >= 0) { return true; }
                }
                return false;
            }
            return Checked.indexOf(this._AsText(RawValue).trim().toLowerCase()) >= 0;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter._MatchesList");
            return true;
        }
    };

    private _NormalizedSelected = (): string[] => {
        if (ks.KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            let CurrValue: string;
            for (CurrValue of this.SelectedValues) {
                Out.push(ks.Nz(CurrValue).trim().toLowerCase());
            }
            return Out;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter._NormalizedSelected");
            return [];
        }
    };

    // Human-readable summary for the active-filter chip bar, e.g.
    // 'Status: Active, Closed' or 'Age > 30'.
    public Describe = (): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const Label: string = ks.Nz(this.Title) || ks.Nz(this.PropName);
            if (this.Operator === kFilterOperator.InList) {
                return Label + ": " + this.SelectedValues.join(", ");
            }
            if (this.Operator === kFilterOperator.IsEmpty) { return Label + ": empty"; }
            if (this.Operator === kFilterOperator.IsNotEmpty) { return Label + ": not empty"; }
            if (this.Operator === kFilterOperator.IsTrue) { return Label + ": yes"; }
            if (this.Operator === kFilterOperator.IsFalse) { return Label + ": no"; }
            if (this.Operator === kFilterOperator.Between) {
                return Label + ": " + this.Value + " – " + this.Value2;
            }
            return Label + " " + kColumnFilter.OperatorSymbol(this.Operator) + " " + this.Value;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter.Describe");
            return "";
        }
    };

    // Plain-data snapshot of this condition (feature #16 save/restore).
    public ToState = (): kColumnFilterState => {
        if (ks.KErrorMode === true) {
            return { PropName: "", ColType: 0, Title: "", Operator: 0, Value: "", Value2: "", SelectedValues: [] };
        }
        try {
            return {
                PropName: this.PropName,
                ColType: this.ColType,
                Title: this.Title,
                Operator: this.Operator,
                Value: this.Value,
                Value2: this.Value2,
                SelectedValues: this.SelectedValues.slice()
            };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter.ToState");
            return { PropName: "", ColType: 0, Title: "", Operator: 0, Value: "", Value2: "", SelectedValues: [] };
        }
    };

    public static FromState = (State: kColumnFilterState): kColumnFilter => {
        if (ks.KErrorMode === true) { return new kColumnFilter(); }
        try {
            const Filter: kColumnFilter = new kColumnFilter();
            Filter.PropName = ks.Nz(State.PropName);
            Filter.ColType = ks.N0(State.ColType);
            Filter.Title = ks.Nz(State.Title);
            Filter.Operator = ks.N0(State.Operator);
            Filter.Value = ks.Nz(State.Value);
            Filter.Value2 = ks.Nz(State.Value2);
            if (Array.isArray(State.SelectedValues) === true) { Filter.SelectedValues = State.SelectedValues.slice(); }
            return Filter;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter.FromState");
            return new kColumnFilter();
        }
    };

    public static OperatorSymbol = (Operator: kFilterOperator): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
        switch (Operator) {
            case kFilterOperator.Equals: return "=";
            case kFilterOperator.NotEquals: return "≠";
            case kFilterOperator.GreaterThan: return ">";
            case kFilterOperator.LessThan: return "<";
            case kFilterOperator.GreaterOrEqual: return "≥";
            case kFilterOperator.LessOrEqual: return "≤";
            case kFilterOperator.StartsWith: return "starts with";
            case kFilterOperator.EndsWith: return "ends with";
            case kFilterOperator.Before: return "before";
            case kFilterOperator.After: return "after";
            case kFilterOperator.OnDate: return "on";
            default: return "contains";
        }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter.OperatorSymbol");
            return "";
        }
    };

    // The operators offered for a column type. Used by the popup/dialog to build
    // the operator dropdown so each type only shows meaningful choices.
    public static OperatorsFor = (ColType: kHTMLEditableColType): kFilterOperator[] => {
        if (ks.KErrorMode === true) { return []; }
        try {
            if (ColType === kHTMLEditableColType.Number || ColType === kHTMLEditableColType.Currency) {
                return [kFilterOperator.Equals, kFilterOperator.NotEquals, kFilterOperator.GreaterThan,
                    kFilterOperator.LessThan, kFilterOperator.GreaterOrEqual, kFilterOperator.LessOrEqual,
                    kFilterOperator.Between, kFilterOperator.IsEmpty, kFilterOperator.IsNotEmpty];
            }
            if (ColType === kHTMLEditableColType.Date || ColType === kHTMLEditableColType.DateTime
                || ColType === kHTMLEditableColType.Time) {
                return [kFilterOperator.OnDate, kFilterOperator.Before, kFilterOperator.After,
                    kFilterOperator.Between, kFilterOperator.IsEmpty, kFilterOperator.IsNotEmpty];
            }
            if (ColType === kHTMLEditableColType.Checkbox || ColType === kHTMLEditableColType.Radio) {
                return [kFilterOperator.IsTrue, kFilterOperator.IsFalse];
            }
            return [kFilterOperator.Contains, kFilterOperator.Equals, kFilterOperator.NotEquals,
                kFilterOperator.StartsWith, kFilterOperator.EndsWith,
                kFilterOperator.IsEmpty, kFilterOperator.IsNotEmpty];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilter.OperatorsFor");
            return [];
        }
    };
}

// The whole filter state for one grid: the per-column conditions (AND-combined)
// plus the global free-text term. ONE concern: decide row visibility and
// surface distinct values; it never touches the DOM directly except by calling
// row.Show()/Hide() through the grid's ApplyFilters. Global multi-word matching
// lives in _RowMatchesGlobal (kSearchItems is collection-oriented and does not
// fit the per-row test); the grid highlights matches with a CSS class so inline
// editing is never disturbed.
export class kEditableTableFilter {
    public ColumnFilters: kColumnFilter[] = [];
    public GlobalTerm: string = "";

    public GetColumnFilter = (PropName: string): kColumnFilter | null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            let CurrFilter: kColumnFilter;
            for (CurrFilter of this.ColumnFilters) {
                if (CurrFilter.PropName === PropName) { return CurrFilter; }
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.GetColumnFilter");
            return null;
        }
    };

    public SetColumnFilter = (Filter: kColumnFilter): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Filter.IsEmptyCondition() === true) { this.ClearColumn(Filter.PropName); return; }
            const Existing: kColumnFilter | null = this.GetColumnFilter(Filter.PropName);
            if (Existing === null) {
                this.ColumnFilters.push(Filter);
                return;
            }
            Existing.ColType = Filter.ColType;
            Existing.Title = Filter.Title;
            Existing.Operator = Filter.Operator;
            Existing.Value = Filter.Value;
            Existing.Value2 = Filter.Value2;
            Existing.SelectedValues = Filter.SelectedValues;
            Existing.Enabled = Filter.Enabled;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.SetColumnFilter");
        }
    };

    public ClearColumn = (PropName: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Kept: kColumnFilter[] = [];
            let CurrFilter: kColumnFilter;
            for (CurrFilter of this.ColumnFilters) {
                if (CurrFilter.PropName !== PropName) { Kept.push(CurrFilter); }
            }
            this.ColumnFilters = Kept;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.ClearColumn");
        }
    };

    public ClearAll = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.ColumnFilters = [];
            this.GlobalTerm = "";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.ClearAll");
        }
    };

    public HasActiveFilters = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (this.ColumnFilters.length > 0) { return true; }
            return ks.Nz(this.GlobalTerm).trim() !== "";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.HasActiveFilters");
            return false;
        }
    };

    // JSON round-trip of the whole filter state (feature #16). The host persists
    // the string (per-user view) and restores it with Deserialize.
    public Serialize = (): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const Columns: kColumnFilterState[] = [];
            let CurrFilter: kColumnFilter;
            for (CurrFilter of this.ColumnFilters) { Columns.push(CurrFilter.ToState()); }
            const State: kFilterState = { Columns: Columns, GlobalTerm: this.GlobalTerm };
            return JSON.stringify(State);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.Serialize");
            return "";
        }
    };

    public Deserialize = (Json: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.ClearAll();
            if (ks.Nz(Json).trim() === "") { return; }
            const State: kFilterState = JSON.parse(Json);
            if (Array.isArray(State.Columns) === true) {
                let CurrState: kColumnFilterState;
                for (CurrState of State.Columns) { this.ColumnFilters.push(kColumnFilter.FromState(CurrState)); }
            }
            this.SetGlobalTerm(ks.Nz(State.GlobalTerm));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.Deserialize");
        }
    };

    public SetGlobalTerm = (Term: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.GlobalTerm = ks.Nz(Term);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.SetGlobalTerm");
        }
    };

    public RowMatches = (Row: kEditableTableRow): boolean => {
        if (ks.KErrorMode === true) { return true; }
        try {
            if (this._RowMatchesColumns(Row) === false) { return false; }
            return this._RowMatchesGlobal(Row);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.RowMatches");
            return true;
        }
    };

    private _RowMatchesColumns = (Row: kEditableTableRow): boolean => {
        if (ks.KErrorMode === true) { return true; }
        try {
            let CurrFilter: kColumnFilter;
            for (CurrFilter of this.ColumnFilters) {
                const RawValue: any = Row.GetValueFromCell(CurrFilter.PropName);
                if (CurrFilter.Matches(RawValue) === false) { return false; }
            }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter._RowMatchesColumns");
            return true;
        }
    };

    // Every whitespace-separated term must appear somewhere in the row's visible
    // text (RichHtml cells contribute their plain text, not their markup).
    private _RowMatchesGlobal = (Row: kEditableTableRow): boolean => {
        if (ks.KErrorMode === true) { return true; }
        try {
            const Term: string = ks.Nz(this.GlobalTerm).trim().toLowerCase();
            if (Term === "") { return true; }
            const Haystack: string = this.RowText(Row).toLowerCase();
            const Words: string[] = Term.split(" ");
            let CurrWord: string;
            for (CurrWord of Words) {
                if (CurrWord === "") { continue; }
                if (Haystack.indexOf(CurrWord) < 0) { return false; }
            }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter._RowMatchesGlobal");
            return true;
        }
    };

    public RowText = (Row: kEditableTableRow): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const Parts: string[] = [];
            let CurrCell: any;
            for (CurrCell of Row.Cells) {
                let Text: string = kColumnFilter.ValueToText(CurrCell.CurrentValue);
                if (CurrCell.ColDef && CurrCell.ColDef.Type === kHTMLEditableColType.RichHtml) {
                    Text = ks.PlainTextFromHTML(Text);
                }
                Parts.push(Text);
            }
            return Parts.join(" ");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.RowText");
            return "";
        }
    };

    // The distinct, natural-sorted display values present in a column across the
    // supplied rows — the candidate list for an Excel-style checklist. Multi-
    // select cells contribute each of their values separately.
    public DistinctValues = (Rows: kEditableTableRow[], Column: kEditableColumn): string[] => {
        if (ks.KErrorMode === true) { return []; }
        try {
            const Seen: { [k: string]: boolean } = {};
            const Out: string[] = [];
            let CurrRow: kEditableTableRow;
            for (CurrRow of Rows) {
                const RawValue: any = CurrRow.GetValueFromCell(Column.KObjectPropertyName);
                this._CollectDistinct(RawValue, Seen, Out);
            }
            Out.sort(this._NaturalCompare);
            return Out;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter.DistinctValues");
            return [];
        }
    };

    private _CollectDistinct = (RawValue: any, Seen: { [k: string]: boolean }, Out: string[]): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Values: any[] = [];
            if (Array.isArray(RawValue) === true) {
                let CurrEntry: any;
                for (CurrEntry of RawValue) { Values.push(CurrEntry); }
            } else {
                Values.push(RawValue);
            }
            let CurrValue: any;
            for (CurrValue of Values) {
                const Text: string = kColumnFilter.ValueToText(CurrValue).trim();
                if (Text === "") { continue; }
                const Key: string = Text.toLowerCase();
                if (Seen[Key] === true) { continue; }
                Seen[Key] = true;
                Out.push(Text);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter._CollectDistinct");
        }
    };

    private _NaturalCompare = (A: string, B: string): number => {
        if (ks.KErrorMode === true) { return 0; }
        try {
            return A.localeCompare(B, undefined, { numeric: true, sensitivity: "base" });
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilter._NaturalCompare");
            return 0;
        }
    };
}
